import { streamQuantityEventName } from '@/components/commonComponents/ExportByColumnModal';
import { IncidentAlertAction, KitStatus } from '@/enums';
import { KitParams as OldKitParams } from '@/interfaces';
import {
  CreateKitEntity,
  KitEntity,
  KitParams,
  MarkKitReceived,
  ReportReadyKitParams,
} from '@/pages/privatePages/kit/types';
import { BulkUpdateKitReportStatusPayload } from '@/queries';
import APIService from './axios.service';
import { BaseService } from './base.service';
import { CustomResponse } from './type';

class KitServiceClass extends BaseService<'kit', KitEntity, CreateKitEntity> {
  constructor() {
    super('kits');
  }

  // "Update customer details": assigns a patient to a kit (Kit page)
  updateCustomerDetails = ({
    serial,
    ...payload
  }: {
    serial: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    address: string;
  }) => {
    return APIService.put(
      `${this.path}/${encodeURIComponent(serial)}/customer-details`,
      payload,
    );
  };

  // Kit ID check for products requiring a source kit (e.g. Optimise Add-on)
  validateSourceKit = (
    serial: string,
  ): Promise<{
    kitId: string;
    kitSerial: string;
    kitReceivedAt: string;
    eligibleUntil: string;
  }> => {
    return APIService.get(
      `${this.path}/${encodeURIComponent(serial)}/source-kit-eligibility`,
    );
  };

  markBulkKitReceived = (
    payload: MarkKitReceived,
  ): Promise<{
    failedKits: string[];
    passedKits: string[];
    receivedKitsData: string[];
  }> => {
    return APIService.put(this.path + '/mark-bulk-kit-received', payload);
  };

  streamKits = (params: OldKitParams): Promise<string> => {
    return APIService.get(this.path + '/stream-kits', {
      params,
      responseType: 'stream',
      onDownloadProgress(data) {
        const responseData = data.event?.target?.response || '';
        // Regex to find all progress values
        const regex = /"progress"\s*:\s*(\d+(\.\d+)?)/g;
        const matches = [...responseData.matchAll(regex)];
        // Get the last match
        const lastMatch = matches[matches.length - 1];
        const lastProgress = lastMatch
          ? Math.round(parseFloat(lastMatch[1]))
          : 0;

        // Dispatch kit quantity for percent progress bar
        window.dispatchEvent(
          new CustomEvent(streamQuantityEventName, {
            detail: { percentProgress: lastProgress },
          }),
        );
      },
    });
    // return fetch('http://localhost:3001/kits/stream-kits', {
    //   headers: {
    //     'Content-Type': 'application/json',
    //     Authorization: `Bearer ${getToken('accessToken')}`,
    //   },
    // });
  };

  markKitsClinicReceived = (payload: {
    orderIds: string[];
    kitIds: string[];
  }): Promise<void> => {
    return APIService.patch(this.path + '/mark-kits-clinic-received', payload);
  };

  checkSerialExists = (serial: string): Promise<{ exists: boolean }> => {
    return APIService.get(this.path + `/${serial}/exists`);
  };

  getKitDetail = (params: KitParams): Promise<KitEntity> => {
    const { serial, ...rest } = params;

    return APIService.get(this.path + `/${serial}`, { params: rest });
  };

  getReportReadyKits = (
    params: ReportReadyKitParams,
  ): Promise<CustomResponse<KitEntity[]>> => {
    return APIService.get(this.path + '/alert-dashboard/report-ready', {
      params,
    });
  };

  getReportSupportKits = (params?: {
    action?: IncidentAlertAction;
  }): Promise<KitEntity[]> => {
    return APIService.get(this.path + '/alert-dashboard/report-support', {
      params,
    });
  };

  getKitForReTriggerPdfGeneration = (
    params: KitParams,
  ): Promise<CustomResponse<KitEntity[]>> => {
    return APIService.get(this.path + '/retrigger-pdf-generation', { params });
  };

  bulkUpdateAllowDnaExtraction(
    payload: {
      serial: string;
      allowDnaExtraction: boolean;
    }[],
  ): Promise<void> {
    return APIService.post(`/kits/bulk-update-allow-dna-extraction`, payload);
  }

  setKitReportStatus(payload: {
    serial: string;
    status: KitStatus;
  }): Promise<void> {
    return APIService.patch(`/kits/${payload.serial}`, {
      status: payload.status,
    });
  }

  bulkUpdateKitReportStatus(
    payload: BulkUpdateKitReportStatusPayload,
  ): Promise<any> {
    return APIService.post(`/kits/bulk-update-kit-status`, payload);
  }
}

export const kitService = new KitServiceClass();
